<template>
  <div v-if="instructors && instructors.length > 0" class="assigned-instructors-section">
    <v-card class="instructors-header-card">
      <v-card-title class="primary white--text">
        <v-icon class="mr-2" color="white">mdi-account-group</v-icon>
        Your Assigned Instructors
      </v-card-title>
    </v-card>

    <v-row class="instructors-grid">
      <v-col v-for="instructor in instructors" :key="instructor.id" cols="12" sm="6" md="4" lg="3">
        <v-card class="instructor-card" elevation="2">
          <v-card-text class="pa-4">
            <div class="instructor-header">
              <v-avatar size="80" class="instructor-avatar">
                <img v-if="instructor.profilePicture" :src="instructor.profilePicture"
                  :alt="`${instructor.firstName} ${instructor.lastName}`" />
                <v-icon v-else size="40" color="primary">mdi-account-circle</v-icon>
              </v-avatar>
              <div class="instructor-badge">
                <v-icon small color="white">mdi-check-circle</v-icon>
              </div>
            </div>

            <div class="instructor-info mt-3">
              <h3 class="instructor-name">
                {{ instructor.firstName }} {{ instructor.lastName }}
              </h3>

              <div v-if="instructor.phone" class="instructor-contact mt-2">
                <v-icon small color="primary" class="mr-1">mdi-phone</v-icon>
                <span class="contact-text">{{ instructor.phone }}</span>
              </div>
            </div>

            <v-divider class="my-3"></v-divider>

            <div class="instructor-actions">
              <v-btn small color="primary" block rounded class="action-btn" @click="handleBookingClick(instructor)">
                <v-icon small left>mdi-calendar-clock</v-icon>
                Request Booking
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <BookingModal v-model="showBookingModal" 
    :key="bookingModalKey" 
    :appointment="currentInstructor"
    :remaining-hours="freeBookings - totalUtilizedHours" 
    :payment-method-added="paymentMethodAdded"
    mode="request"
    @submit="handleBookingSubmit" 
    @error="(msg) => $toast.error(msg)" />

    <v-dialog v-model="showPaymentWarning" max-width="500">
      <v-card>
        <v-card-title class="headline">Payment Required</v-card-title>
        <v-card-text>
          <v-alert type="warning" dense class="mb-4 text-center">
            Please add payment method. You have used your free bookings.
          </v-alert>
          <div class="text-center">
            <v-btn color="primary" @click="goToPayment">
              Add Payment Method
            </v-btn>
            <v-btn text @click="showPaymentWarning = false" class="ml-2">
              Cancel
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Parse from 'parse'
import { mapActions } from 'vuex'
import BookingModal from '@/components/dashboard/BookingModal.vue'

export default {
  name: 'AssignedInstructors',
  props: {
    instructors: {
      type: Array,
      default: () => []
    },
    schoolPackageId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showBookingModal: false,
      bookingModalKey: 0,
      currentInstructor: null,
      freeBookings: 0,
      totalUtilizedHours: 0,
      paymentMethodAdded: false,
      showPaymentWarning: false
    }
  },
  components: {
    BookingModal
  },
  computed: {
    canBookMoreLessons() {
      return (
        this.totalUtilizedHours < this.freeBookings ||
        this.paymentMethodAdded
      )
    }
  },
  methods: {
    async handleBookingClick(instructor) {
      await this.checkBookingEligibility()

      if (this.canBookMoreLessons) {
        this.openBookingModal(instructor)
      } else {
        this.showPaymentWarning = true
      }
    },
    goToPayment() {
      this.$router.push('/add-payment')
      this.showPaymentWarning = false
    },
    openBookingModal(instructor) {
      this.currentInstructor = instructor
      this.showBookingModal = true
      this.bookingModalKey++
    },
    async checkBookingEligibility() {
      try {
        const studentId = Parse.User.current().id
        const student = await new Parse.Query('_User').get(studentId)

        const packageId = student.get('packageId')
        if (packageId) {
          const packageData = await new Parse.Query('SchoolPackages').get(packageId)
          this.freeBookings = parseInt(packageData?.get('freeBookings')) || 0
        }

        this.paymentMethodAdded = student.get('stripePaymentMethodAdded') || false
        this.totalUtilizedHours = await this.getUtilizedHours(studentId)
      } catch (error) {
        console.error('Error checking booking eligibility:', error)
      }
    },
    async getUtilizedHours(studentId) {
      try {
        const rows = await new Parse.Query('Appointments')
          .equalTo('studentId', new Parse.Object('_User', { id: studentId }))
          .containedIn('status', ['Finished', 'Approved'])
          .find()

        let totalHours = 0
        rows.forEach((item) => {
          totalHours += parseFloat(item.get('hoursLesson')) || 0
        })

        return totalHours
      } catch (error) {
        console.error('Error calculating utilized hours:', error)
        return 0
      }
    },
    buildStudentAddress(student) {
      const addressTwo = student.get('addressTwo')
      const addressOne = student.get('addressOne')
      const city = student.get('city')
      const province = student.get('province')
      const postalCode = student.get('postalCode')

      let studentAddress = ''
      if (addressTwo) {
        studentAddress += addressTwo + ' '
      }
      if (addressOne) {
        studentAddress += addressOne + ' '
      }
      if (city) {
        studentAddress += city + ' '
      }
      if (province) {
        studentAddress += province + ' '
      }
      if (postalCode) {
        studentAddress += postalCode
      }

      return studentAddress.trim()
    },
    async handleBookingSubmit(formData) {
      try {
        await this.checkBookingEligibility()
        if (!this.canBookMoreLessons) {
          if (this.$toast && this.$toast.error) {
            this.$toast.error('Please add a payment method. You have used your package hours.')
          }
          return
        }

        const lessonHours = parseFloat(formData.lessonHours || 1)
        const remainingFree = this.freeBookings - this.totalUtilizedHours
        if (lessonHours > remainingFree && !this.paymentMethodAdded) {
          if (this.$toast && this.$toast.error) {
            this.$toast.error(
              `You only have ${Math.max(0, remainingFree)} free hour(s) remaining. Add a payment method or choose fewer hours.`
            )
          }
          return
        }

        const student = Parse.User.current()
        const instructorId = this.currentInstructor?.id
        if (!instructorId) {
          if (this.$toast && this.$toast.error) {
            this.$toast.error('Instructor not found. Please try again.')
          }
          return
        }

        let start = null
        if (formData.date && formData.startTime) {
          const selectedDate = new Date(formData.date)
          const [startHour, startMinute] = formData.startTime.split(':').map(Number)
          start = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            startHour,
            startMinute
          )
        }

        const bookingData = {
          status: 'Pending',
          notes: formData.notes || '',
          appoinmentLocation: this.buildStudentAddress(student),
          parking: false,
          highwayDriving: false,
          innerCityDrivingTurns: false,
          roadtestOptions: '',
          paid: false,
          paymentMethod: 'online',
          hoursLesson: String(lessonHours),
          drivingLessonDates: [],
          type: ['booking_lesson']
        }

        const Appointments = Parse.Object.extend('Appointments')
        const newBooking = new Appointments()
        Object.entries(bookingData).forEach(([key, value]) => {
          newBooking.set(key, value)
        })

        const studentPointer = new Parse.Object('_User')
        studentPointer.id = student.id
        newBooking.set('studentId', studentPointer)

        const instructorPointer = new Parse.Object('_User')
        instructorPointer.id = instructorId
        newBooking.set('instructorId', instructorPointer)

        const instructorPackage = await new Parse.Query('InstructorPackages')
          .equalTo('instructorId', instructorPointer)
          .first()
        if (instructorPackage) {
          const instructorPackagePointer = new Parse.Object('InstructorPackages')
          instructorPackagePointer.id = instructorPackage.id
          newBooking.set('instructorPackageId', instructorPackagePointer)
        }

        if (this.schoolPackageId) {
          const schoolPackagePointer = new Parse.Object('SchoolPackages')
          schoolPackagePointer.id = this.schoolPackageId
          newBooking.set('schoolPackageId', schoolPackagePointer)
        }

        await newBooking.save()

        if (this.$toast && this.$toast.success) {
          this.$toast.success('Booking saved successfully!')
        }
        this.showBookingModal = false
        this.bookingModalKey++

        await this.sendBookingNotifications(start, student, instructorId, {
          lessonHours,
          notes: formData.notes || ''
        })

        this.$router.push('/student-bookings')
      } catch (error) {
        console.error('Error saving new booking:', error)
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Failed to save booking. Please try again.')
        }
      }
    },
    async sendBookingNotifications(start, student, instructorId, meta = {}) {
      const lessonHours = meta.lessonHours
      const notes = (meta.notes && String(meta.notes).trim()) || ''
      let instructorPhone = ''
      let studentName = ''
      let instructorName = ''

      try {
        const instructorQuery = new Parse.Query('_User')
        const instructor = await instructorQuery.get(instructorId)
        instructorPhone = instructor.get('phone') || ''
        const instructorFirst = instructor.get('firstName') || ''
        const instructorLast = instructor.get('lastName') || ''
        instructorName = `${instructorFirst} ${instructorLast}`.trim()

        const studentFirst = student.get('firstName') || ''
        const studentLast = student.get('lastName') || ''
        studentName = `${studentFirst} ${studentLast}`.trim()

        const hasValidStart = start && !isNaN(new Date(start).getTime())
        const formattedDate = hasValidStart
          ? new Date(start).toLocaleString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })
          : null

        const hoursLabel =
          Number(lessonHours) === 1 ? '1 hour' : `${lessonHours} hours`
        const noteSuffix = notes
          ? ` Student note: ${notes.length > 100 ? `${notes.slice(0, 97)}...` : notes}`
          : ''

        let instructorMessage = ''
        if (hasValidStart && formattedDate) {
          instructorMessage =
            `DriverX: ${studentName} submitted a lesson request (${hoursLabel}) with preferred time ${formattedDate}. Please review and confirm in the DriverX app.` +
            noteSuffix
        } else {
          instructorMessage =
            `DriverX: ${studentName} submitted a new driving lesson request. Please open the DriverX app to review and schedule.` +
            noteSuffix
        }

        if (instructorPhone) {
          await Parse.Cloud.run('sendAppointmentSMS', {
            to: instructorPhone,
            message: instructorMessage
          })

          const adminMessage = hasValidStart
            ? `DriverX admin: Lesson request from ${studentName} (${hoursLabel}, ${start.toLocaleString()}) for instructor ${instructorName} (${instructorPhone}).`
            : `DriverX admin: Lesson request from ${studentName} for instructor ${instructorName} (${instructorPhone}).`
          await Parse.Cloud.run('sendSMS', {
            message: adminMessage
          })
        } else {
          console.warn('Instructor phone not found, SMS not sent.')
        }

        const deviceId = instructor.get('deviceId')
        if (deviceId) {
          await this.sendPushNotification({
            player_id: deviceId,
            message: hasValidStart
              ? `New lesson request from ${studentName} (${hoursLabel}). Preferred: ${formattedDate}.`
              : `New lesson request from ${studentName} (${hoursLabel}). Open DriverX to review.`,
            type: 'appmnt',
            senderId: student.id,
            name: studentName
          })
        }

        const instructorEmail = instructor.get('customEmail')
        if (instructorEmail) {
          try {
            await Parse.Cloud.run('newAppointmentEmail', {
              email: instructorEmail,
              requesterName: studentName
            })
          } catch (e) {
            console.warn('newAppointmentEmail:', e)
          }
        }
      } catch (smsError) {
        console.error('Failed to fetch user info or send SMS:', smsError)
      }
    }
  }
}
</script>

<style scoped>
.assigned-instructors-section {
  margin-top: 24px;
  margin-bottom: 24px;
}

.instructors-header-card {
  margin-bottom: 20px;
  border-radius: 12px 12px 0 0;
}

.instructors-grid {
  margin: 0;
}

.instructor-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e5e7eb;
}

.instructor-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.instructor-header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.instructor-avatar {
  border: 3px solid rgba(45, 154, 71, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.instructor-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: rgba(45, 154, 71, 1);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.instructor-info {
  text-align: center;
}

.instructor-name {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.4;
}

.instructor-location,
.instructor-contact {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.location-text,
.contact-text {
  font-size: 13px;
  color: #6b7280;
  word-break: break-word;
}

.instructor-actions {
  margin-top: auto;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Mobile Responsive Styles */
@media (max-width: 600px) {
  .assigned-instructors-section {
    margin-top: 16px;
    margin-bottom: 16px;
  }

  .instructors-header-card {
    margin-bottom: 16px;
  }

  .instructor-card {
    margin-bottom: 12px;
  }

  .instructor-avatar {
    width: 70px !important;
    height: 70px !important;
  }

  .instructor-name {
    font-size: 16px;
  }

  .instructor-badge {
    width: 24px;
    height: 24px;
    top: -6px;
    right: -6px;
  }

  .instructor-badge .v-icon {
    font-size: 14px;
  }

  .location-text,
  .contact-text {
    font-size: 12px;
  }

  .action-btn {
    font-size: 13px;
    padding: 8px 16px;
  }
}

@media (max-width: 960px) and (min-width: 601px) {
  .instructor-card {
    margin-bottom: 16px;
  }
}

/* Tablet and Desktop */
@media (min-width: 961px) {
  .instructors-grid {
    gap: 20px;
  }
}
</style>
